import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SectionHeader from "@/components/SectionHeader";
import { BookOpen, Pencil, Trash2, Plus, Save, X } from "lucide-react";

export default function Docs() {
  const [docs, setDocs] = useState([]);
  const [editing, setEditing] = useState(null); // {id?, title, body, order}
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = () => api.get("/docs").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startEdit = (d) => setEditing({ id: d.id, title: d.title, body: d.body, order: d.order ?? 0 });
  const startCreate = () => setEditing({ id: null, title: "", body: "", order: docs.length + 1 });
  const cancel = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    const payload = { title: editing.title, body: editing.body, order: Number(editing.order) || 0 };
    if (editing.id) {
      await api.put(`/docs/${editing.id}`, payload);
    } else {
      await api.post("/docs", payload);
    }
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Usunąć ten dokument?")) return;
    await api.delete(`/docs/${id}`);
    load();
  };

  return (
    <div className="max-w-5xl mx-auto px-5 py-12" data-testid="docs-page">
      <SectionHeader title="Regulamin i wiedza" />
      <p className="text-center mt-4 mb-10" style={{ color: "var(--sr-cream-soft)" }}>
        Wszystko co musi znać członek klubu — regulamin, zasady kawalkady, znaki na drodze.
      </p>

      {isAdmin && !editing && (
        <div className="flex justify-end mb-5">
          <button onClick={startCreate} className="btn-fire flex items-center gap-2" data-testid="add-doc-inline-btn">
            <Plus size={16} /> Nowy dokument
          </button>
        </div>
      )}

      {/* Edit / create form */}
      {editing && (
        <form onSubmit={save} className="sr-card p-6 mb-8 fade-up" data-testid="doc-edit-form">
          <h3 className="font-display text-xl text-[var(--sr-cream)] mb-4 flex items-center gap-2">
            <Pencil size={18} className="text-[var(--sr-fire)]" />
            {editing.id ? "Edytuj dokument" : "Nowy dokument"}
          </h3>
          <div className="grid sm:grid-cols-[1fr_120px] gap-4 mb-4">
            <div>
              <label className="sr-label">Tytuł</label>
              <input
                className="sr-input"
                required
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                data-testid="doc-edit-title"
                autoFocus
              />
            </div>
            <div>
              <label className="sr-label">Kolejność</label>
              <input
                type="number"
                className="sr-input"
                value={editing.order}
                onChange={(e) => setEditing({ ...editing, order: e.target.value })}
                data-testid="doc-edit-order"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="sr-label">Treść</label>
            <textarea
              className="sr-input"
              rows={10}
              required
              value={editing.body}
              onChange={(e) => setEditing({ ...editing, body: e.target.value })}
              placeholder="Pisz co chcesz. Podwójny enter = nowy akapit."
              data-testid="doc-edit-body"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" className="btn-ghost flex items-center gap-2" onClick={cancel} data-testid="doc-edit-cancel">
              <X size={14} /> Anuluj
            </button>
            <button type="submit" className="btn-fire flex items-center gap-2" data-testid="doc-edit-save">
              <Save size={14} /> Zapisz
            </button>
          </div>
        </form>
      )}

      {docs.length === 0 && !editing ? (
        <p className="text-center" style={{ color: "var(--sr-muted)" }}>
          Brak dokumentów. {isAdmin && "Kliknij 'Nowy dokument' żeby dodać pierwszy."}
        </p>
      ) : (
        <div className="space-y-5">
          {docs.map((d, i) => (
            <article key={d.id} className="sr-card p-6 md:p-8 fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`doc-${d.id}`}>
              <div className="flex items-start gap-3">
                <BookOpen className="text-[var(--sr-fire)] flex-shrink-0 mt-1" size={22} />
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-xl md:text-2xl text-[var(--sr-cream)]">{d.title}</h3>
                    {isAdmin && (
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => startEdit(d)}
                          className="btn-ghost text-xs flex items-center gap-1 !py-2 !px-3"
                          data-testid={`edit-doc-${d.id}`}
                          title="Edytuj"
                        >
                          <Pencil size={12} /> Edytuj
                        </button>
                        <button
                          onClick={() => remove(d.id)}
                          className="text-[var(--sr-muted)] hover:text-red-400 p-2"
                          data-testid={`delete-doc-${d.id}`}
                          title="Usuń"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                  <div
                    className="mt-4 text-base leading-relaxed whitespace-pre-wrap"
                    style={{ color: "var(--sr-cream-soft)" }}
                  >
                    {d.body}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
